// Enums
export enum ServiceType {
  BrainMassage = 'BrainMassage',
  HydroMassage = 'HydroMassage',
}

export enum BookingStep {
  Location = 1,
  Service = 2,
  DateTime = 3,
  Account = 4,
  Summary = 5,
}

/** First step shown when changing an existing booking (location is pre-filled). */
export const CHANGE_BOOKING_START_STEP = BookingStep.Service;

export enum BookingStatus {
  Confirmed = 'confirmed',
  Cancelled = 'cancelled',
  CheckedIn = 'checkedIn',
  Completed = 'completed',
  NoShow = 'noShow',
}

// Location models
export interface ServiceUnit {
  uuid: string;
  code: string;
  name: string;
  serviceType: ServiceType;
  isActive?: boolean; // May be added in future API versions
  createdAt?: string;
  updatedAt?: string | null;
}

export interface Location {
  uuid: string;
  name: string;
  code: string;
  imageUrl?: string;
  addressLine1?: string;
  addressLine2?: string;
  houseNumber?: string;
  postalCode?: string;
  city?: string;
  country?: string;
  latitude?: number;
  longitude?: number;
  phone?: string;
  email?: string;
  timezone?: string;
  /** Inclusive start of the bookable window (YYYY-MM-DD). Null/unset = no lower bound. */
  bookableFrom?: string | null;
  /** Inclusive end of the bookable window (YYYY-MM-DD). Null/unset = no upper bound. */
  bookableTo?: string | null;
  openingTime?: string; // May be added in future API versions
  closingTime?: string; // May be added in future API versions
  serviceUnits?: ServiceUnit[];
  createdAt?: string;
  updatedAt?: string;
}

export interface LocationsResponse {
  data: Location[];
}

export interface LocationResponse {
  data: Location;
}

// Service models
export interface ServiceLocation {
  uuid: string;
  code?: string;
  name?: string;
  normalPrice?: number;
  bundlePrice?: number;
  currency: string;
  isActive?: boolean; // May be added in future API versions
}

export interface Service {
  uuid: string;
  code: string;
  name: string;
  description?: string;
  imageUrl?: string;
  serviceType: ServiceType;
  durationMinutes: number;
  normalPrice?: number;
  bundlePrice?: number;
  currency?: string;
  createdAt?: string;
  updatedAt?: string | null;
  locations?: ServiceLocation[];
}

export interface ServicesResponse {
  data: Service[];
}

export interface ServiceResponse {
  data: Service;
}

// Availability models
export interface AvailabilityRequest {
  locationUuid: string;
  mainServiceUuid: string;
  addOnServiceUuid?: string;
  date: string; // YYYY-MM-DD
  numberOfPeople: number;
  excludeBookingUuid?: string;
}

export interface AvailableServiceSlot {
  uuid: string;
  code: string;
  name: string;
  startTime: string;
  endTime: string;
  availableUnits: ServiceUnit[];
}

export interface AvailableSlot {
  startTime: string;
  endTime: string;
  mainService: AvailableServiceSlot;
  addOnService?: AvailableServiceSlot | null;
}

export interface AvailabilityResponse {
  data: {
    date: string;
    locationUuid: string;
    numberOfPeople: number;
    totalDuration: number;
    availableSlots: AvailableSlot[];
  };
}

// Booking models
export interface BookingLocation {
  uuid: string;
  name: string;
  code: string;
}

export interface BookingService {
  uuid: string;
  name: string;
  code: string;
  serviceType?: ServiceType;
  durationMinutes?: number;
  imageUrl?: string;
  startTime?: string;
  endTime?: string;
  units?: ServiceUnit[];
}

export interface BookingBase {
  uuid: string;
  code: string;
  firstName: string;
  lastName: string;
  customerEmail?: string;
  customerPhone?: string;
  startDatetime: string; // ISO format date-time
  endDatetime: string; // ISO format date-time
  numberOfPeople: number;
  status: BookingStatus;
  totalPrice?: number;
  baseTotalPrice?: number;
  currency: string;
  notes?: string;
  createdAt: string;
  updatedAt?: string;
  location: BookingLocation;
  mainService: BookingService;
  addOnService?: BookingService | null;
}

export type Booking = BookingBase;

/**
 * Booking as returned by the access-code endpoints (`bff-booking-by-code-*`).
 * Reduced PII: first name only, no last name, email or phone.
 */
export type GuestBooking = Omit<
  BookingBase,
  'lastName' | 'customerEmail' | 'customerPhone'
>;

export interface GuestBookingResponse {
  data: GuestBooking;
}

/** Contact fields cannot be changed through the access code. */
export interface UpdateBookingByCodeRequest {
  locationUuid: string;
  mainServiceUuid: string;
  addOnServiceUuid?: string;
  startDatetime: string; // ISO format
  numberOfPeople: number;
  notes?: string;
}

export interface BookingsResponse {
  data: Booking[];
}

export interface BookingResponse {
  data: Booking;
}

export interface CreateBookingRequest {
  locationUuid: string;
  mainServiceUuid: string;
  addOnServiceUuid?: string;
  firstName: string;
  lastName: string;
  customerEmail: string;
  customerPhone?: string;
  startDatetime: string; // ISO format
  numberOfPeople: number;
  currency: string; // Required by backend (e.g., "EUR")
  notes?: string;
}

export interface UpdateBookingRequest {
  locationUuid?: string;
  mainServiceUuid?: string;
  addOnServiceUuid?: string;
  firstName?: string;
  lastName?: string;
  customerEmail?: string;
  customerPhone?: string;
  startDatetime?: string; // ISO format
  numberOfPeople?: number;
  currency?: string;
  notes?: string;
}

// Query parameters for booking list
export interface BookingsQueryParams {
  locationUuid?: string;
  dateFrom?: string; // YYYY-MM-DD
  dateTo?: string; // YYYY-MM-DD
  serviceType?: ServiceType;
  status?: BookingStatus;
}

export type BookingData = {
  location: Location | null;
  mainService: Service | null;
  addOnService: Service | null;
  guests: number;
  date: string | null;
  timeSlot: AvailableSlot | null;
  customerDetails: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  };
  /** True when the user is checking out as a guest (session-only draft). */
  isGuestCheckout: boolean;
  notes: string;
  totalPrice: number;
};

export const DEFAULT_BOOKING_DATA: BookingData = {
  location: null,
  mainService: null,
  addOnService: null,
  guests: 1,
  date: null,
  timeSlot: null,
  customerDetails: {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  },
  isGuestCheckout: false,
  notes: '',
  totalPrice: 0,
};
